import { supabase } from './supabase';
import { CloudflareR2Service } from './cloudflareR2Service';
import { ImageOptimizer, OptimizedImage } from './imageOptimizer';

export interface UserGalleryUpload {
  id: string;
  userId: string;
  originalFilename: string;
  optimizedFilename: string;
  fileUrl: string;
  fileSize: number;
  originalSize?: number;
  optimizedSize?: number;
  compressionRatio?: number;
  uploadTimestamp: string;
  createdAt: string;
  updatedAt: string;
}

export interface UploadResult {
  success: boolean;
  upload?: UserGalleryUpload;
  error?: string;
}

export class UserGalleryService {
  private r2Service: CloudflareR2Service;

  constructor() {
    this.r2Service = CloudflareR2Service.fromEnvironment();
  }

  /**
   * Uploads and optimizes a user image to their gallery
   */
  async uploadUserImage(file: File, userId: string): Promise<UploadResult> {
    try {
      console.log('🖼️ Starting user gallery upload...', file.name);

      // Step 1: Validate the file
      if (!ImageOptimizer.isValidImageFile(file)) {
        return {
          success: false,
          error: 'Unsupported file format. Please upload JPG, JPEG, PNG, HEIC, or HEIF images.'
        };
      }

      if (!ImageOptimizer.validateFileSize(file, 50)) {
        return {
          success: false,
          error: 'File size too large. Please upload images smaller than 50MB.'
        };
      }

      // Step 2: Optimize the image
      console.log('⚡ Optimizing image...');
      const optimizedResult: OptimizedImage = await ImageOptimizer.optimizeImage(file);

      // Step 3: Upload to R2 storage
      console.log('☁️ Uploading to cloud storage...');
      const uploadResult = await this.r2Service.uploadFile(optimizedResult.file, {
        prefix: 'user-gallery',
        userId: userId,
        metadata: {
          'upload-type': 'user-gallery',
          'original-filename': file.name,
          'original-size': optimizedResult.originalSize.toString(),
          'optimized-size': optimizedResult.optimizedSize.toString(),
          'compression-ratio': optimizedResult.compressionRatio.toString()
        }
      });

      // Step 4: Save metadata to database
      console.log('💾 Saving to database...');
      const { data, error } = await supabase
        .from('user_gallery_uploads')
        .insert({
          user_id: userId,
          original_filename: file.name,
          optimized_filename: optimizedResult.file.name,
          file_url: uploadResult.url,
          file_size: optimizedResult.optimizedSize,
          original_size: optimizedResult.originalSize,
          optimized_size: optimizedResult.optimizedSize,
          compression_ratio: optimizedResult.compressionRatio
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Database save failed:', error);
        return {
          success: false,
          error: 'Failed to save image metadata to database.'
        };
      }

      console.log('✅ User gallery upload complete!');
      
      return {
        success: true,
        upload: {
          id: data.id,
          userId: data.user_id,
          originalFilename: data.original_filename,
          optimizedFilename: data.optimized_filename,
          fileUrl: data.file_url,
          fileSize: data.file_size,
          originalSize: data.original_size,
          optimizedSize: data.optimized_size,
          compressionRatio: data.compression_ratio,
          uploadTimestamp: data.upload_timestamp,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        }
      };

    } catch (error) {
      console.error('❌ User gallery upload failed:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      return {
        success: false,
        error: `Upload failed: ${errorMessage}`
      };
    }
  }

  /**
   * Gets all user gallery uploads for a specific user
   */
  async getUserUploads(userId: string): Promise<UserGalleryUpload[]> {
    try {
      const { data, error } = await supabase
        .from('user_gallery_uploads')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Failed to fetch user uploads:', error);
        return [];
      }

      return (data || []).map(item => ({
        id: item.id,
        userId: item.user_id,
        originalFilename: item.original_filename,
        optimizedFilename: item.optimized_filename,
        fileUrl: item.file_url,
        fileSize: item.file_size,
        originalSize: item.original_size,
        optimizedSize: item.optimized_size,
        compressionRatio: item.compression_ratio,
        uploadTimestamp: item.upload_timestamp,
        createdAt: item.created_at,
        updatedAt: item.updated_at
      }));

    } catch (error) {
      console.error('❌ Error fetching user uploads:', error);
      return [];
    }
  }

  /**
   * Deletes a user gallery upload
   */
  async deleteUserUpload(uploadId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_gallery_uploads')
        .delete()
        .eq('id', uploadId)
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Failed to delete user upload:', error);
        return false;
      }

      console.log('✅ User upload deleted successfully');
      return true;

    } catch (error) {
      console.error('❌ Error deleting user upload:', error);
      return false;
    }
  }

  /**
   * Creates a singleton instance
   */
  static getInstance(): UserGalleryService {
    return new UserGalleryService();
  }
}

// Export singleton instance
export const userGalleryService = UserGalleryService.getInstance();